import type { ReactNode } from 'react';
import { Navigate } from 'react-router';
import type { Permission } from '@mys/contracts';
import { useSession } from '../lib/session.js';
import { useCan } from '../lib/permissions.js';

/* ============================================================================
   The gate on a whole screen.
   ----------------------------------------------------------------------------
   Replaces RequireRole, which compared the role to the single string 'owner'.
   That was right while there were two roles and wrong the moment there were
   six: /invoices/new is not about being the owner, it is about being allowed
   to raise an invoice, and those stopped being the same sentence in slice 1.3.

   Like RequireAuth, this is a COURTESY and not a security boundary. The API's
   `requirePermission` is what actually refuses; anyone can edit the JavaScript
   running on their own machine. What it buys is honesty: /403 is better than a
   form that answers 403 after it has been filled in.
   ========================================================================== */

export function RequirePermission({
  permission,
  children,
}: {
  permission: Permission;
  children: ReactNode;
}) {
  const { status } = useSession();
  const may = useCan();

  /* RequireAuth is outside this and handles the redirect to /login. Rendering
     nothing here avoids a flash of /403 while `me` is still in flight. */
  if (status !== 'authenticated') return null;

  if (!may(permission)) return <Navigate to="/403" replace />;

  return <>{children}</>;
}
